import type { Slide, SlideContent, SlideType } from '@sab/core/schemas';
import { formatDateID, formatIDR, formatIDRCompact } from '@sab/core/utils/format.ts';
import { SlideChrome } from './SlideChrome.tsx';
import { Logo } from './Logo.tsx';

export interface SlideRendererContext {
  total: number;
  companyName: string;
  date: string; // ISO
  contactEmail?: string;
  contactName?: string;
  contactPhone?: string;
}

interface ToneByType {
  tone: 'cream' | 'accent' | 'ink' | 'elevated';
  chromeVariant: 'navy' | 'light';
}

function toneFor(slide: Slide): ToneByType {
  // Alternating editorial rhythm — cover/contact accent, pricing ink, others cream/elevated.
  switch (slide.type) {
    case 'cover':
      return { tone: 'cream', chromeVariant: 'navy' };
    case 'pricing':
      return { tone: 'ink', chromeVariant: 'light' };
    case 'contact':
    case 'next-steps':
      return { tone: 'accent', chromeVariant: 'light' };
    case 'why-matters':
    case 'difference':
      return { tone: 'ink', chromeVariant: 'light' };
    default:
      // Alternating cream / elevated every other slide
      return slide.index % 2 === 0
        ? { tone: 'cream', chromeVariant: 'navy' }
        : { tone: 'elevated', chromeVariant: 'navy' };
  }
}

function dataTone(tone: ToneByType['tone']): string | undefined {
  return tone === 'elevated' ? undefined : tone;
}

export function SlideRenderer({ slide, ctx }: { slide: Slide; ctx: SlideRendererContext }) {
  const tone = toneFor(slide);
  const dataAttr = dataTone(tone.tone);

  return (
    <section
      className={`sab-slide sab-slide--${slide.type}`}
      data-slide-id={slide.id}
      data-slide-type={slide.type}
      {...(dataAttr ? { 'data-tone': dataAttr } : {})}
      aria-labelledby={`slide-${slide.index}-headline`}
    >
      {renderBody(slide.type, slide.content, ctx, slide.index)}
      <SlideChrome
        index={slide.index}
        total={ctx.total}
        variant={tone.chromeVariant}
        companyName={ctx.companyName}
      />
    </section>
  );
}

function renderBody(type: SlideType, c: SlideContent, ctx: SlideRendererContext, index: number) {
  const headlineId = `slide-${index}-headline`;

  if (type === 'cover') {
    return (
      <>
        <div className="sab-cover__brandbar">
          <Logo variant="navy" />
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-fg-muted)' }}>
            Proposal · Performance Lead Generation
          </span>
        </div>
        {c.eyebrow ? <p className="sab-eyebrow">{c.eyebrow}</p> : null}
        <h1 id={headlineId} className="sab-headline sab-headline--hero">
          {c.headline}
        </h1>
        {c.subheadline ? <p className="sab-subheadline">{c.subheadline}</p> : null}
        <div className="sab-cover__date">
          Disusun untuk {ctx.companyName} · {formatDateID(ctx.date)}
        </div>
      </>
    );
  }

  if (type === 'pricing' && c.pricing) {
    const { pricing } = c;
    return (
      <>
        <hr className="sab-rule" />
        {c.eyebrow ? <p className="sab-eyebrow">{c.eyebrow}</p> : null}
        <h2 id={headlineId} className="sab-headline">
          {c.headline}
        </h2>
        {c.subheadline ? <p className="sab-subheadline">{c.subheadline}</p> : null}
        <div className="sab-pricing">
          <div>
            <span className="sab-pricing__tier">{tierLabel(pricing.tier)}</span>
            <div className="sab-pricing__price">
              {formatIDRCompact(pricing.monthlyIDR)}
              <span className="sab-pricing__suffix">/ bulan</span>
            </div>
            <p className="sab-body" style={{ marginTop: 'var(--space-4)' }}>
              Komitmen {pricing.commitmentMonths} bulan
              {pricing.expectedLeadsMin && pricing.expectedLeadsMax
                ? ` · Target ${pricing.expectedLeadsMin}–${pricing.expectedLeadsMax} lead/bulan`
                : ''}
              .
            </p>
            <p style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)', opacity: 0.7 }}>
              ({formatIDR(pricing.monthlyIDR)} / bulan)
            </p>
          </div>
          <ul className="sab-pricing__includes" aria-label="Paket termasuk">
            {pricing.includes.map((i, k) => (
              <li key={k}>{i}</li>
            ))}
          </ul>
        </div>
      </>
    );
  }

  if (type === 'case-study' && c.caseStudy) {
    const cs = c.caseStudy;
    return (
      <>
        <hr className="sab-rule" />
        {c.eyebrow ? <p className="sab-eyebrow">{c.eyebrow}</p> : null}
        <h2 id={headlineId} className="sab-headline">
          {c.headline}
        </h2>
        <div className="sab-case">
          <div className="sab-case__meta">
            <span className="sab-case__client">{cs.client}</span>
            <span className="sab-case__industry">{cs.industry}</span>
          </div>
          <div className="sab-case__row">
            <div className="sab-case__block">
              <h4>Tantangan</h4>
              <p>{cs.challenge}</p>
            </div>
            <div className="sab-case__block">
              <h4>Yang SAB lakukan</h4>
              <p>{cs.solution}</p>
            </div>
            <div className="sab-case__block">
              <h4>Hasil</h4>
              <p>{cs.result}</p>
            </div>
          </div>
        </div>
        {cs.metrics.length > 0 ? <MetricsRow metrics={cs.metrics} /> : null}
      </>
    );
  }

  if (type === 'process') {
    return (
      <>
        <hr className="sab-rule" />
        {c.eyebrow ? <p className="sab-eyebrow">{c.eyebrow}</p> : null}
        <h2 id={headlineId} className="sab-headline">
          {c.headline}
        </h2>
        {c.subheadline ? <p className="sab-subheadline">{c.subheadline}</p> : null}
        <ol className="sab-timeline" aria-label="Proses SAB">
          {c.bullets.map((b, k) => {
            const [title, ...rest] = b.split(' — ');
            const desc = rest.join(' — ');
            return (
              <li className="sab-timeline__step" key={k}>
                <span className="sab-timeline__num">{k + 1}</span>
                <h3 className="sab-timeline__title">{title}</h3>
                {desc ? <p className="sab-timeline__desc">{desc}</p> : null}
              </li>
            );
          })}
        </ol>
      </>
    );
  }

  if (type === 'timeline') {
    return (
      <>
        <hr className="sab-rule" />
        {c.eyebrow ? <p className="sab-eyebrow">{c.eyebrow}</p> : null}
        <h2 id={headlineId} className="sab-headline">
          {c.headline}
        </h2>
        {c.subheadline ? <p className="sab-subheadline">{c.subheadline}</p> : null}
        <ol className="sab-timeline" aria-label="Timeline">
          {c.bullets.map((b, k) => {
            const [title, ...rest] = b.split(' — ');
            const desc = rest.join(' — ');
            return (
              <li className="sab-timeline__step" key={k}>
                <span className="sab-timeline__num">{k + 1}</span>
                <h3 className="sab-timeline__title">{title}</h3>
                {desc ? <p className="sab-timeline__desc">{desc}</p> : null}
              </li>
            );
          })}
        </ol>
      </>
    );
  }

  if (type === 'contact') {
    return (
      <>
        <hr className="sab-rule" />
        {c.eyebrow ? <p className="sab-eyebrow">{c.eyebrow}</p> : null}
        <h2 id={headlineId} className="sab-headline">
          {c.headline}
        </h2>
        {c.subheadline ? <p className="sab-subheadline">{c.subheadline}</p> : null}
        <ul className="sab-bullets">
          {ctx.contactName ? (
            <li className="sab-bullets__item">
              <strong>{ctx.contactName}</strong>
              {ctx.contactEmail ? ` — ${ctx.contactEmail}` : ''}
            </li>
          ) : null}
          {ctx.contactPhone ? <li className="sab-bullets__item">{ctx.contactPhone}</li> : null}
          {c.bullets.map((b, k) => (
            <li className="sab-bullets__item" key={k}>
              {b}
            </li>
          ))}
        </ul>
        {c.cta ? (
          <div style={{ marginTop: 'var(--space-10)' }}>
            <a
              href={c.cta.href ?? '#'}
              style={{
                display: 'inline-block',
                padding: 'var(--space-3) var(--space-6)',
                borderRadius: 'var(--radius-full)',
                background: 'var(--sab-stone-0)',
                color: 'var(--sab-navy-700)',
                fontWeight: 'var(--weight-semibold)',
                fontSize: 'var(--text-base)',
              }}
            >
              {c.cta.label}
            </a>
          </div>
        ) : null}
      </>
    );
  }

  // Default body — works for opportunity, problem, why-matters, sab-intro, services,
  // difference, approach, strategy, expected-results, why-sab, next-steps, appendix, custom
  return (
    <>
      <hr className="sab-rule" />
      {c.eyebrow ? <p className="sab-eyebrow">{c.eyebrow}</p> : null}
      <h2 id={headlineId} className="sab-headline">
        {c.headline}
      </h2>
      {c.subheadline ? <p className="sab-subheadline">{c.subheadline}</p> : null}
      {c.body ? <p className="sab-body">{c.body}</p> : null}
      {c.bullets.length > 0 ? (
        <ul className="sab-bullets">
          {c.bullets.map((b, k) => (
            <li className="sab-bullets__item" key={k}>
              {b}
            </li>
          ))}
        </ul>
      ) : null}
      {c.metrics.length > 0 ? <MetricsRow metrics={c.metrics} /> : null}
    </>
  );
}

function MetricsRow({ metrics }: { metrics: SlideContent['metrics'] }) {
  return (
    <div className="sab-metrics" role="list" aria-label="Metrik">
      {metrics.map((m, i) => (
        <div className="sab-metric" role="listitem" key={i}>
          <span className="sab-metric__value">
            {m.value}
            {m.delta ? (
              <span className="sab-metric__delta" data-tone={m.tone}>
                {m.delta}
              </span>
            ) : null}
          </span>
          <span className="sab-metric__label">{m.label}</span>
        </div>
      ))}
    </div>
  );
}

function tierLabel(tier: 'startup' | 'growth' | 'scale' | 'enterprise'): string {
  return {
    startup: 'Paket Startup',
    growth: 'Paket Growth',
    scale: 'Paket Scale',
    enterprise: 'Paket Enterprise',
  }[tier];
}
